/**
 * Sidebar mega menu: primary categories, optional secondary menu, product panels.
 */
if (!customElements.get('mega-menu-sidebar')) {
  customElements.define(
    'mega-menu-sidebar',
    class MegaMenuSidebar extends HTMLElement {
      connectedCallback() {
        this.#primaryTriggers = this.querySelectorAll('[data-primary-trigger]');
        this.#secondaryColumn = this.querySelector('[data-secondary-column]');
        this.#secondaryNavs = this.querySelectorAll('[data-secondary-nav]');
        this.#categoryTriggers = this.querySelectorAll('[data-category-trigger]');
        this.#panels = this.querySelectorAll('[data-category-panel]');

        if (!this.#panels.length) return;

        this.addEventListener('mouseover', this.#onPointerOver);
        this.addEventListener('focusin', this.#onFocusIn);

        if (this.#primaryTriggers.length) {
          const firstPrimary = this.#primaryTriggers[0];
          if (firstPrimary instanceof HTMLElement) {
            this.#activatePrimary(firstPrimary.dataset.primaryId, { activateFirstSecondary: true });
          }
        } else if (this.#categoryTriggers.length) {
          const firstPanel = this.#categoryTriggers[0];
          if (firstPanel instanceof HTMLElement) {
            this.#activatePanel(firstPanel.dataset.panelId);
          }
        }
      }

      disconnectedCallback() {
        this.removeEventListener('mouseover', this.#onPointerOver);
        this.removeEventListener('focusin', this.#onFocusIn);
      }

      /** @type {NodeListOf<HTMLElement>} */
      #primaryTriggers = [];

      /** @type {HTMLElement | null} */
      #secondaryColumn = null;

      /** @type {NodeListOf<HTMLElement>} */
      #secondaryNavs = [];

      /** @type {NodeListOf<HTMLElement>} */
      #categoryTriggers = [];

      /** @type {NodeListOf<HTMLElement>} */
      #panels = [];

      /** @param {MouseEvent} event */
      #onPointerOver = (event) => {
        if (!(event.target instanceof Element)) return;

        const secondaryTrigger = event.target.closest('[data-category-trigger][data-primary-id]');
        if (secondaryTrigger instanceof HTMLElement) {
          this.#activatePrimary(secondaryTrigger.dataset.primaryId, {
            activateFirstSecondary: false,
          });
          this.#activatePanel(secondaryTrigger.dataset.panelId);
          return;
        }

        const primaryTrigger = event.target.closest('[data-primary-trigger]');
        if (primaryTrigger instanceof HTMLElement) {
          this.#activatePrimary(primaryTrigger.dataset.primaryId, { activateFirstSecondary: true });
          return;
        }

        const blockTrigger = event.target.closest('[data-category-trigger]:not([data-primary-id])');
        if (blockTrigger instanceof HTMLElement) {
          this.#activatePanel(blockTrigger.dataset.panelId);
        }
      };

      /** @param {FocusEvent} event */
      #onFocusIn = (event) => {
        if (!(event.target instanceof Element)) return;

        const secondaryTrigger = event.target.closest('[data-category-trigger][data-primary-id]');
        if (secondaryTrigger instanceof HTMLElement) {
          this.#activatePrimary(secondaryTrigger.dataset.primaryId, {
            activateFirstSecondary: false,
          });
          this.#activatePanel(secondaryTrigger.dataset.panelId);
          return;
        }

        const primaryTrigger = event.target.closest('[data-primary-trigger]');
        if (primaryTrigger instanceof HTMLElement) {
          this.#activatePrimary(primaryTrigger.dataset.primaryId, { activateFirstSecondary: true });
          return;
        }

        const blockTrigger = event.target.closest('[data-category-trigger]:not([data-primary-id])');
        if (blockTrigger instanceof HTMLElement) {
          this.#activatePanel(blockTrigger.dataset.panelId);
        }
      };

      /**
       * @param {string | undefined} primaryId
       * @param {{ activateFirstSecondary?: boolean }} options
       */
      #activatePrimary(primaryId, options = {}) {
        if (!primaryId) return;

        const { activateFirstSecondary = true } = options;

        let activePrimary = null;

        this.#primaryTriggers.forEach((trigger) => {
          const isActive = trigger.dataset.primaryId === primaryId;
          if (isActive) activePrimary = trigger;
          trigger.classList.toggle('is-active', isActive);
          trigger.toggleAttribute('aria-current', isActive);
        });

        if (!activePrimary) return;

        const primaryItem = this.querySelector(
          `[data-primary-item][data-primary-id="${primaryId}"]`
        );
        const hasSecondary = primaryItem?.dataset.hasSecondary === 'true';

        if (this.#secondaryColumn) {
          this.#secondaryColumn.hidden = !hasSecondary;
        }

        this.#secondaryNavs.forEach((nav) => {
          const show = hasSecondary && nav.dataset.primaryId === primaryId;
          nav.hidden = !show;
        });

        if (hasSecondary && activateFirstSecondary) {
          const secondaryNav = this.querySelector(
            `[data-secondary-nav][data-primary-id="${primaryId}"]`
          );
          const firstSecondary = secondaryNav?.querySelector('[data-category-trigger]');
          if (firstSecondary instanceof HTMLElement) {
            this.#activatePanel(firstSecondary.dataset.panelId);
            return;
          }
        }

        this.#activatePanel(primaryId);
      }

      /**
       * @param {string | undefined} panelId
       */
      #activatePanel(panelId) {
        if (!panelId) return;

        let matchedTrigger = false;

        this.#primaryTriggers.forEach((trigger) => {
          const isActive =
            trigger.dataset.panelId === panelId || trigger.dataset.primaryId === panelId;
          if (isActive) matchedTrigger = true;
          trigger.classList.toggle('is-active', isActive);
          trigger.toggleAttribute('aria-current', isActive);
        });

        this.#categoryTriggers.forEach((trigger) => {
          const isActive = trigger.dataset.panelId === panelId;
          if (isActive) matchedTrigger = true;
          trigger.classList.toggle('is-active', isActive);
          trigger.toggleAttribute('aria-current', isActive);
        });

        let matchedPanel = false;

        this.#panels.forEach((panel) => {
          const isActive = panel.dataset.panelId === panelId;
          if (isActive) matchedPanel = true;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        if (!matchedPanel && !matchedTrigger) return;
      }
    }
  );
}
